import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/_public/privacy-policy")({
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation();

	return (
		<div className="container mx-auto py-8 px-4 max-w-4xl">
			<div className="prose prose-lg max-w-none">
				<h1 className="text-4xl font-bold mb-6 text-gray-900">
					{t("privacy.title")}
				</h1>

				<p className="text-sm text-gray-600 mb-8">
					{t("privacy.lastUpdated")}: {new Date().toLocaleDateString()}
				</p>

				{/* Introduction */}
				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4 text-gray-800">
						{t("privacy.sections.introduction.title")}
					</h2>
					<p className="text-gray-700 leading-relaxed">
						{t("privacy.sections.introduction.content")}
					</p>
				</section>

				{/* Legal Basis */}
				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4 text-gray-800">
						{t("privacy.sections.legalBasis.title")}
					</h2>
					<p className="text-gray-700 leading-relaxed mb-4">
						{t("privacy.sections.legalBasis.content")}
					</p>
					<ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
						{(
							t("privacy.sections.legalBasis.bases", {
								returnObjects: true,
							}) as string[]
						).map((basis) => (
							<li key={basis}>{basis}</li>
						))}
					</ul>
				</section>

				{/* Data Collection */}
				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4 text-gray-800">
						{t("privacy.sections.dataCollection.title")}
					</h2>
					<p className="text-gray-700 leading-relaxed mb-4">
						{t("privacy.sections.dataCollection.content")}
					</p>
					<div className="ml-4">
						<h3 className="text-xl font-medium mb-3 text-gray-800">
							{t("privacy.sections.dataCollection.personalData.title")}
						</h3>
						<ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
							{(
								t("privacy.sections.dataCollection.personalData.items", {
									returnObjects: true,
								}) as string[]
							).map((item) => (
								<li key={item}>{item}</li>
							))}
						</ul>

						<h3 className="text-xl font-medium mb-3 text-gray-800">
							{t("privacy.sections.dataCollection.healthData.title")}
						</h3>
						<ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
							{(
								t("privacy.sections.dataCollection.healthData.items", {
									returnObjects: true,
								}) as string[]
							).map((item) => (
								<li key={item}>{item}</li>
							))}
						</ul>

						<div className="bg-blue-50 p-4 rounded-lg mb-4">
							<h4 className="text-lg font-medium mb-2 text-blue-800">
								{t(
									"privacy.sections.dataCollection.healthData.specialProtections.title",
								)}
							</h4>
							<p className="text-blue-700 mb-3">
								{t(
									"privacy.sections.dataCollection.healthData.specialProtections.content",
								)}
							</p>
							<ul className="list-disc list-inside text-blue-700 space-y-1">
								{(
									t(
										"privacy.sections.dataCollection.healthData.specialProtections.protections",
										{ returnObjects: true },
									) as string[]
								).map((protection) => (
									<li key={protection}>{protection}</li>
								))}
							</ul>
						</div>

						<h3 className="text-xl font-medium mb-3 text-gray-800">
							{t("privacy.sections.dataCollection.usageData.title")}
						</h3>
						<ul className="list-disc list-inside text-gray-700 space-y-2">
							{(
								t("privacy.sections.dataCollection.usageData.items", {
									returnObjects: true,
								}) as string[]
							).map((item) => (
								<li key={item}>{item}</li>
							))}
						</ul>
					</div>
				</section>

				{/* Data Use */}
				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4 text-gray-800">
						{t("privacy.sections.dataUse.title")}
					</h2>
					<p className="text-gray-700 leading-relaxed mb-4">
						{t("privacy.sections.dataUse.content")}
					</p>
					<ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
						{(
							t("privacy.sections.dataUse.purposes", {
								returnObjects: true,
							}) as string[]
						).map((purpose) => (
							<li key={purpose}>{purpose}</li>
						))}
					</ul>
				</section>

				{/* Data Retention */}
				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4 text-gray-800">
						{t("privacy.sections.dataRetention.title")}
					</h2>
					<p className="text-gray-700 leading-relaxed mb-4">
						{t("privacy.sections.dataRetention.content")}
					</p>
					<ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mb-6">
						{(
							t("privacy.sections.dataRetention.periods", {
								returnObjects: true,
							}) as string[]
						).map((period) => (
							<li key={period}>{period}</li>
						))}
					</ul>
					<div className="bg-gray-50 p-4 rounded-lg">
						<h3 className="text-xl font-medium mb-3 text-gray-800">
							{t("privacy.sections.dataRetention.criteria.title")}
						</h3>
						<p className="text-gray-700 mb-3">
							{t("privacy.sections.dataRetention.criteria.content")}
						</p>
						<ul className="list-disc list-inside text-gray-700 space-y-2">
							{(
								t("privacy.sections.dataRetention.criteria.conditions", {
									returnObjects: true,
								}) as string[]
							).map((condition) => (
								<li key={condition}>{condition}</li>
							))}
						</ul>
					</div>
				</section>

				{/* Data Sharing */}
				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4 text-gray-800">
						{t("privacy.sections.dataSharing.title")}
					</h2>
					<p className="text-gray-700 leading-relaxed mb-4">
						{t("privacy.sections.dataSharing.content")}
					</p>
					<ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
						{(
							t("privacy.sections.dataSharing.cases", {
								returnObjects: true,
							}) as string[]
						).map((case_) => (
							<li key={case_}>{case_}</li>
						))}
					</ul>
				</section>

				{/* Data Security */}
				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4 text-gray-800">
						{t("privacy.sections.dataSecurity.title")}
					</h2>
					<p className="text-gray-700 leading-relaxed mb-4">
						{t("privacy.sections.dataSecurity.content")}
					</p>
					<ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
						{(
							t("privacy.sections.dataSecurity.measures", {
								returnObjects: true,
							}) as string[]
						).map((measure) => (
							<li key={measure}>{measure}</li>
						))}
					</ul>
				</section>

				{/* User Rights */}
				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4 text-gray-800">
						{t("privacy.sections.userRights.title")}
					</h2>
					<p className="text-gray-700 leading-relaxed mb-4">
						{t("privacy.sections.userRights.content")}
					</p>
					<ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
						{(
							t("privacy.sections.userRights.rights", {
								returnObjects: true,
							}) as string[]
						).map((right) => (
							<li key={right}>{right}</li>
						))}
					</ul>
				</section>

				{/* Cookies */}
				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4 text-gray-800">
						{t("privacy.sections.cookies.title")}
					</h2>
					<p className="text-gray-700 leading-relaxed">
						{t("privacy.sections.cookies.content")}
					</p>
				</section>

				{/* Contact */}
				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4 text-gray-800">
						{t("privacy.sections.contact.title")}
					</h2>
					<p className="text-gray-700 leading-relaxed">
						{t("privacy.sections.contact.content")}
					</p>
				</section>

				{/* Updates */}
				<section className="mb-8">
					<h2 className="text-2xl font-semibold mb-4 text-gray-800">
						{t("privacy.sections.updates.title")}
					</h2>
					<p className="text-gray-700 leading-relaxed">
						{t("privacy.sections.updates.content")}
					</p>
				</section>
			</div>
		</div>
	);
}
